import { type ChangeEvent, useRef, useState } from "react";
import { FileText, FileUp } from "lucide-react";
import AiScreeningNotice from "@cle/components/AiScreeningNotice";
import CleButton from "@cle/components/CleButton";
import CleModal, {
  CleModalRoot,
  CleModalTrigger,
} from "@cle/components/CleModal";
import { MAX_BATCH_FILES } from "@cle/constants/courseCertificateUpload";

type CourseCertificateUploadDialogProps = {
  isUploading: boolean;
  onFilesSelected: (files: File[]) => void;
};

/** File picker only. Progress, errors and cancellation live in the tab, so the
 * dialog can close the moment the files are handed over. */
const CourseCertificateUploadDialog = ({
  isUploading,
  onFilesSelected,
}: CourseCertificateUploadDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChooseFiles = () => {
    fileInputRef.current?.click();
  };

  const handleFilesChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);

    // Reset synchronously, before the dialog unmounts its content: otherwise
    // re-picking the same files never fires another change event.
    event.target.value = "";

    if (files.length === 0) {
      return;
    }

    setIsOpen(false);
    onFilesSelected(files);
  };

  return (
    <CleModalRoot controller={{ open: isOpen, setOpen: setIsOpen }}>
      <CleModalTrigger asChild>
        <CleButton
          id="cle-upload-course-certificate"
          variant="outline"
          size="sm"
          disabled={isUploading}
        >
          <FileUp size={17} strokeWidth={2} />
          Upload kursusbeviser
        </CleButton>
      </CleModalTrigger>
      <CleModal
        title="Upload kursusbeviser"
        description="Upload PDF-kursusbeviser til AI-analyse."
        size="sm"
        footer={
          <div className="d-flex justify-content-center gap-3 w-100 px-3 py-2">
            <CleButton
              id="cle-cancel-course-certificate-upload"
              variant="outline"
              onClick={() => setIsOpen(false)}
            >
              Afbryd
            </CleButton>
            <CleButton
              id="cle-choose-course-certificate-files"
              onClick={handleChooseFiles}
            >
              Vælg filer
            </CleButton>
          </div>
        }
      >
        <div className="d-flex flex-column align-items-center text-center gap-3">
          <span
            className="cle-compliance-doc-dashboard__empty-icon mb-0"
            aria-hidden="true"
          >
            <FileText size={24} strokeWidth={1.75} />
          </span>
          <div>
            <h3 className="fs-5 fw-semibold mb-2">Vælg dine kursusbeviser</h3>
            <p className="text-muted mb-0">
              Du kan uploade op til {MAX_BATCH_FILES} PDF-filer af gangen.
              Upload kun gyldige kursusbeviser.
            </p>
          </div>
          <p className="text-muted small mb-0">
            <AiScreeningNotice subject="Kursusbeviserne" />
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            multiple
            className="visually-hidden"
            onChange={handleFilesChange}
          />
        </div>
      </CleModal>
    </CleModalRoot>
  );
};

export default CourseCertificateUploadDialog;
